// ===========================================
// Lesson Durations Management Page
// ===========================================
// CRUD interface for managing lesson durations

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useLessonDurations,
  useCreateLessonDuration,
  useUpdateLessonDuration,
  useDeleteLessonDuration,
} from '../../hooks/useAdmin';
import { LessonDuration } from '../../api/admin.api';

// ===========================================
// COMPONENT
// ===========================================

export default function DurationsPage() {
  const { data: durations, isLoading, error } = useLessonDurations();
  const createMutation = useCreateLessonDuration();
  const updateMutation = useUpdateLessonDuration();
  const deleteMutation = useDeleteLessonDuration();

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDuration, setEditingDuration] = useState<LessonDuration | null>(null);
  const [formData, setFormData] = useState({
    minutes: '',
    isActive: true,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [durationToDelete, setDurationToDelete] = useState<LessonDuration | null>(null);

  // Filter durations
  const filteredDurations = useMemo(() => {
    if (!durations) return [];

    if (hideInactive) {
      return durations.filter((duration) => duration.isActive);
    }

    return durations;
  }, [durations, hideInactive]);

  // Table columns
  const columns: Column<LessonDuration>[] = [
    {
      id: 'minutes',
      label: 'Duration',
      minWidth: 200,
      format: (value) => `${value} minutes`,
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 100,
      align: 'right',
      format: (value) =>
        value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        ),
    },
  ];

  // Handlers
  const handleAdd = () => {
    setEditingDuration(null);
    setFormData({ minutes: '', isActive: true });
    setModalOpen(true);
  };

  const handleEdit = (duration: LessonDuration) => {
    setEditingDuration(duration);
    setFormData({
      minutes: duration.minutes.toString(),
      isActive: duration.isActive,
    });
    setModalOpen(true);
  };

  const handleDelete = (duration: LessonDuration) => {
    setDurationToDelete(duration);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        minutes: parseInt(formData.minutes, 10),
        isActive: formData.isActive,
      };

      if (editingDuration) {
        await updateMutation.mutateAsync({
          id: editingDuration.id,
          data,
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  // Check if duration can be deleted (must be inactive)
  const canDeleteDuration = (duration: LessonDuration) => !duration.isActive;

  const handleConfirmDelete = async () => {
    if (durationToDelete) {
      try {
        await deleteMutation.mutateAsync(durationToDelete.id);
        setDeleteDialogOpen(false);
        setDurationToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Lesson Durations"
        subtitle="Manage available lesson lengths"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Durations' },
        ]}
        actionLabel="Add Duration"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load durations. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive durations"
        />

        {filteredDurations.length !== (durations?.length ?? 0) && (
          <Chip
            label={`Showing ${filteredDurations.length} of ${durations?.length ?? 0} durations`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <DataTable
        columns={columns}
        data={filteredDurations}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canDelete={canDeleteDuration}
        deleteDisabledTooltip="Set status to Inactive to delete this duration"
        emptyMessage={
          durations && durations.length > 0
            ? 'No durations match the current filter.'
            : "No durations configured. Click 'Add Duration' to create one."
        }
        searchPlaceholder="Search durations..."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingDuration ? 'Edit Duration' : 'Add Duration'}
        submitLabel={editingDuration ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Duration (minutes)"
            type="number"
            placeholder="e.g., 45"
            value={formData.minutes}
            onChange={(e) => setFormData({ ...formData, minutes: e.target.value })}
            required
            fullWidth
            inputProps={{ min: 15, step: 15 }}
            helperText="Enter duration in minutes (e.g., 30, 45, 60)"
          />
          {editingDuration && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Duration"
        message={`Are you sure you want to delete the ${durationToDelete?.minutes} minute duration? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
